import { ref, provide, inject, isRef } from '@vue/composition-api'
import useVuelidate from '@vuelidate/core'
import reactiveSet from '@/utils/reactive-set'
import { DefaultUpdateInterface, UseDynamicFormInterface } from './DynamicFormsTypes'

const nextIdState = ref(performance.now())

export default function useDynamicForm ({
  questions,
  template,
  path,
  formValue = ref({}),
  updateImpl = defaultUpdate,
  onUpdated = () => {},
  formId: customFormId,
  disableActions = ref(false),
  readonly = ref(false),
  useDefaults = ref(false),
}: UseDynamicFormInterface) {
  const questionsRef = isRef(questions) ? questions : ref(questions)
  const templateRef = isRef(template) ? template : ref(template)
  const resetTriggerCounter = ref(0)

  const formId = customFormId || `form_${nextIdState.value}`
  nextIdState.value++

  provide(`${formId}_update`, update)
  provide(`${formId}_value`, formValue)
  provide(`${formId}_questions`, questionsRef)
  provide(`${formId}_template`, templateRef)
  provide(`${formId}_path`, isRef(path) ? path : ref(path))
  const isReadonly = inject('dynamic_form_readonly', readonly)
  const isDisablingActions = inject('dynamic_form_actions_disabled', disableActions)
  const isDefaultsActive = inject('dynamic_form_defaults_active', useDefaults)
  provide('dynamic_form_defaults_active', isDefaultsActive)
  provide('dynamic_form_actions_disabled', isDisablingActions)
  provide('dynamic_form_readonly', isReadonly)
  provide(`dynamic_form_reset_trigger_counter`, resetTriggerCounter)

  const injectedOnUpdated = inject('dynamic_form_on_updated', () => {})

  function chainedOnUpdated (params: { formValue: any } = { formValue }) {
    // NOTE: Call with local params for direct parent
    onUpdated(params)
    // NOTE: Trigger injected onUpdated. It will default to parent’s formValue.
    //       For Root form that will be the root formValue
    injectedOnUpdated()
  }

  provide('dynamic_form_on_updated', chainedOnUpdated)

  function update ({ property, value }: { property: string, value: any }) {
    updateImpl({ property, value, formValue }, defaultUpdate)
    // NOTE: By this time all formValue has been updated, including the root formValue
    chainedOnUpdated({ formValue })
  }

  function resetValue () {
    formValue.value = {}
    resetTriggerCounter.value++
  }

  // @ts-expect-error
  const $v = useVuelidate()

  return {
    formValue,
    update,
    resetValue,
    $v,
    formId,
  }
}

function defaultUpdate ({ formValue, property, value }: DefaultUpdateInterface): void {
  // NOTE: This might throw a Vue WARN about not mutating props.
  // This is to be expected and is here to handle a special
  // case where we need to have an object but get a null as the form value
  // TODO: this line should be deleted after all Default Modals have been migrated to Dynamic Forms
  if (formValue.value === null) formValue.value = {}
  reactiveSet(formValue.value, property, value)
}
